import { initReactI18next } from 'react-i18next';
import i18n from 'i18next';
import Cookies from 'js-cookie';
import { CONFIG } from './config/Config';
import languagesData from '../languages.json';

// Define type interfaces for i18n resources
interface ResourceNamespace {
  [key: string]: Record<string, any>;
}

interface Resources {
  [language: string]: ResourceNamespace;
}

// --------------------- Constants ------------------------

// Re-export languages for tests and other imports
const languages = languagesData;
// Define namespaces
const namespaces = ['common', 'questions'];
// Define constants for localStorage
const STORAGE_LANGUAGE_KEY = 'app_user_language';

// Generate resources dynamically
const resources: Resources = languagesData.reduce(
  (acc: Resources, lang: string) => {
    // Initialize each language with empty objects for each namespace
    acc[lang] = namespaces.reduce(
      (namespaceAcc: ResourceNamespace, namespace) => {
        namespaceAcc[namespace] = {};
        return namespaceAcc;
      },
      {}
    );
    return acc;
  },
  {}
);

// --------------------- methods ------------------------

// Utility function to get URL parameters
const getUrlParams = () => {
  if (typeof window === 'undefined') {
    return new URLSearchParams();
  }
  return new URLSearchParams(window.location.search);
};

// Utility function to get browser language
const getBrowserLanguage = () => {
  if (typeof navigator === 'undefined') {
    return undefined;
  }
  return navigator.language;
};

const saveLanguage = (language: string) => {
  // save in local storage
  try {
    localStorage.setItem(STORAGE_LANGUAGE_KEY, language);
  } catch (error) {
    console.warn('Failed to save language to localStorage:', error);
  }
  // set it also in cookie in case of local storage not available
  try {
    Cookies.set(STORAGE_LANGUAGE_KEY, language);
  } catch (error) {
    console.error('Failed to change language:', error);
  }
};

const getSavedLanguage = () => {
  // try to find it from local storage
  try {
    console.log('checking local storage');
    if (localStorage.getItem(STORAGE_LANGUAGE_KEY))
      return localStorage.getItem(STORAGE_LANGUAGE_KEY) as string;
  } catch (error) {
    console.warn('Failed to read language from localStorage:', error);
  }
  // then try to find it from cookie
  try {
    console.log('checking cookie');
    return Cookies.get(STORAGE_LANGUAGE_KEY);
  } catch (error) {
    console.warn('Failed to read language from cookie:', error);
  }
};

const detectUserLanguage = (): string | undefined => {
  // 1. Recover it from url
  const urlParams = getUrlParams();
  const urlLanguage = urlParams.get('lang');
  if (urlLanguage && languages.includes(urlLanguage)) {
    saveLanguage(urlLanguage);
    return urlLanguage;
  } else {
    console.warn('Language not part of available languages:', urlLanguage);
  }

  // 2. Recover it from saved language
  const savedLanguage = getSavedLanguage();
  if (savedLanguage) {
    return savedLanguage;
  }

  // 3. Recover it from browser language
  const browserLanguage = getBrowserLanguage();
  if (browserLanguage && languages.includes(browserLanguage)) {
    saveLanguage(browserLanguage);
    return browserLanguage;
  }

  // if nothing is found, we return undefined
  return undefined;
};

// change language
const changeLanguage = (language: string) => {
  saveLanguage(language);
  // then we reload, we also change the query param from url
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('lang', language);
    window.history.pushState(
      {},
      '',
      `${window.location.pathname}?${urlParams.toString()}`
    );
    window.location.reload();
  }
};

// Function to dynamically load namespaces for a given language
const loadNamespaces = async (language: string, namespaces: string[]) => {
  try {
    for (const namespace of namespaces) {
      try {
        const module = await import(
          `./assets/lang/${language}/${namespace}.json`
        );
        i18n.addResourceBundle(language, namespace, module.default, true, true);
      } catch (error) {
        console.warn(
          `Failed to load namespace "${namespace}" for language "${language}":`,
          error
        );
        // If the namespace doesn't exist for the requested language, try to load it from the fallback language
        if (language !== 'en') {
          try {
            const fallbackModule = await import(
              `./assets/lang/en/${namespace}.json`
            );
            i18n.addResourceBundle(
              language,
              namespace,
              fallbackModule.default,
              true,
              true
            );
          } catch (fallbackError) {
            console.error(
              `Failed to load fallback namespace "${namespace}" for language "en":`,
              fallbackError
            );
          }
        }
      }
    }
  } catch (error) {
    console.error('Error loading namespaces:', error);
  }
};

// --------------------- implementation ------------------------

// Detect user language
let userLanguage = detectUserLanguage();
// warn if langue is not part of languages
if (userLanguage && !languages.includes(userLanguage)) {
  console.warn('Language not part of available languages:', userLanguage);
  userLanguage = 'en';
}
const defaultLanguage = userLanguage && !CONFIG.ONLY_EN ? userLanguage : 'en';

// Initialize i18n
i18n.use(initReactI18next).init({
  resources,
  lng: defaultLanguage, // Use saved language or default to 'en'
  fallbackLng: 'en',
  ns: namespaces,
  defaultNS: namespaces[0],
  interpolation: {
    escapeValue: false, // React already escapes values
  },
});
// not sure if this is needed
i18n.language = defaultLanguage; // Set the initial language

i18n.on('languageChanged', (language) => {
  loadNamespaces(language, namespaces);
  saveLanguage(language);
});

// Initial load of resources for current language
loadNamespaces(defaultLanguage, namespaces);

// --------------------- exports ------------------------

export { languages, changeLanguage };
export default i18n;
